import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { FiArchive, FiMoreVertical, FiRotateCcw } from 'react-icons/fi'
import {
  Grid,
  GridItem,
  Tr,
  Td,
  Flex,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton
} from '@chakra-ui/react'
import { Table, Spinner, Alert } from '../UI'
import {
  canManageDiscountArchive,
  FormatDate,
  FormatMoney,
  GetNotification,
  getDiscountArchiveActorName
} from '../../Utils'
import {
  useDiscounts,
  useMutateDiscounts
} from '../../hooks/Discounts'

const monthOrder = {
  "Enero": 1,
  "Febrero": 2,
  "Marzo": 3,
  "Abril": 4,
  "Mayo": 5,
  "Junio": 6,
  "Julio": 7,
  "Agosto": 8,
  "Septiembre": 9,
  "Octubre": 10,
  "Noviembre": 11,
  "Diciembre": 12
}

const TableDiscountsPartners = ({
  filtersDiscounts,
  limit,
  tableSize,
  allowArchiveManagement = false
}) => {
  const [page, setPage] = useState(0)
  const { user } = useSelector((state) => state.auth)
  const canManageArchive =
    allowArchiveManagement && canManageDiscountArchive(user)
  const showArchived = Boolean(filtersDiscounts.archived)
  const {
    data: discountsList,
    isLoading,
    isFetching,
    isError
  } = useDiscounts(filtersDiscounts, page, limit)
  const { mutate: archiveDiscount, isLoading: isArchiving } =
    useMutateDiscounts('ARCHIVE')
  const { mutate: restoreDiscount, isLoading: isRestoring } =
    useMutateDiscounts('RESTORE')
  const activeHeaders = [
    '#',
    'Fecha de Creación',
    'Fecha de Descuento',
    'Monto Descontado',
    'Observaciones',
    'Comprobantes'
  ]
  const archivedHeaders = [
    ...activeHeaders,
    'Fecha de Archivado',
    'Archivado por',
    'Motivo'
  ]
  const headers = [
    ...(showArchived ? archivedHeaders : activeHeaders),
    ...(canManageArchive ? ['Acciones'] : [])
  ]

  const sortData = (data = []) => {
    return [...data].sort((a, b) => {
      const dateA = parseInt(a.year) * 100 + monthOrder[a.month]
      const dateB = parseInt(b.year) * 100 + monthOrder[b.month];
      return dateB - dateA
    })
  }

  const getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || fallback

  const handleArchive = (discount, reason) => {
    archiveDiscount(
      { discountId: discount._id, reason },
      {
        onSuccess: () =>
          GetNotification.basic('Descuento archivado con éxito', 'Aceptar'),
        onError: (error) =>
          GetNotification.basic(
            'Error',
            'Aceptar',
            getErrorMessage(error, 'No fue posible archivar el descuento'),
            'error'
          )
      }
    )
  }

  const questionArchive = (discount) => {
    GetNotification.questionWithInput(
      '¿Archivar este descuento?',
      (reason) => handleArchive(discount, reason),
      'El descuento dejará de aparecer en la lista de registros activos.',
      'Motivo del archivado'
    )
  }

  const handleRestore = (discount) => {
    restoreDiscount(discount._id, {
      onSuccess: () =>
        GetNotification.basic('Descuento restaurado con éxito', 'Aceptar'),
      onError: (error) =>
        GetNotification.basic(
          'Error',
          'Aceptar',
          getErrorMessage(error, 'No fue posible restaurar el descuento'),
          'error'
        )
    })
  }

  const questionRestore = (discount) => {
    GetNotification.question(
      '¿Restaurar este descuento?',
      () => handleRestore(discount),
      null,
      'Volverá a aparecer en la lista de descuentos activos.',
      'question'
    )
  }

  useEffect(() => {
    setPage(0)
  }, [showArchived])

  return (
    <Grid templateColumns={'repeat(1, 1fr)'} w={'100%'}>
      <Flex w={'100%'} justify={'flex-end'} py={3}>
        <Spinner size={'lg'} show={isLoading || isFetching} />
      </Flex>
      {isError && (
        <Flex w={'100%'} justify={'flex-end'} py={3}>
          <Alert status={'error'} message={'Error al Cargar los Datos'} />
        </Flex>
      )}
      <GridItem
        overflowX={'auto'}
        display={{
          lg: 'table',
          md: 'break-word',
          sm: 'break-word'
        }}
        css={{
          '&::-webkit-scrollbar': {
            width: '4px'
          },
          '&::-webkit-scrollbar-track': {
            width: '6px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#2E7423',
            borderRadius: '20px'
          }
        }}
      >
        <Table
          headers={headers}
          totalRecords={discountsList?.totalRecords}
          setPage={setPage}
          page={page}
          size={tableSize}
          limit={limit}
        >
          {sortData(discountsList?.currentPages || [])?.map((item, index) => (
            <Tr key={item._id}>
              <Td fontWeight={700} color={'grey.500'} textAlign={'center'}>
                {index + 1 + page * limit}
              </Td>
              <Td color={'grey.500'}>{FormatDate(`${item?.createdAt}`)}</Td>
              <Td color={'grey.500'}>
                {item?.month} {item?.year}
              </Td>
              <Td color={'grey.500'}>$ {FormatMoney(item?.amount)}</Td>
              <Td color={'grey.500'}>
                {item?.observations || 'Sin observaciones'}
              </Td>
              <Td color={'grey.500'}>
                {item?.support?.map((file) => (
                  <Badge
                    key={file.url}
                    colorScheme={'green'}
                    mr={2}
                    fontSize="0.8em"
                    cursor={'pointer'}
                    onClick={() => window.open(file.url, '_blank')}
                  >
                    {file.name}
                  </Badge>
                ))}
              </Td>
              {showArchived && (
                <>
                  <Td color={'grey.500'}>
                    {item?.archivedAt
                      ? FormatDate(`${item.archivedAt}`)
                      : 'Registro histórico'}
                  </Td>
                  <Td color={'grey.500'}>
                    {getDiscountArchiveActorName(item)}
                  </Td>
                  <Td color={'grey.500'}>
                    {item?.archiveReason || 'Auditoría no disponible'}
                  </Td>
                </>
              )}
              {canManageArchive && (
                <Td textAlign={'center'}>
                  <Menu isLazy>
                    <MenuButton
                      as={IconButton}
                      aria-label={'Acciones del descuento'}
                      icon={<FiMoreVertical />}
                      variant={'ghost'}
                      isDisabled={isArchiving || isRestoring}
                    />
                    <MenuList minW={'180px'}>
                      <MenuItem
                        icon={showArchived ? <FiRotateCcw /> : <FiArchive />}
                        onClick={() =>
                          showArchived
                            ? questionRestore(item)
                            : questionArchive(item)
                        }
                      >
                        {showArchived ? 'Restaurar' : 'Archivar'}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              )}
            </Tr>
          ))}
        </Table>
      </GridItem>
    </Grid>
  )
}

export default TableDiscountsPartners
